import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "./ContentContext";
import { useTx, txtStyle, multiline } from "./siteText";
import { useLightbox } from "./Lightbox";

export const HappyHour = ({ variant = "home" }) => {
  const { settings, resolveImg } = useContent();
  const tx = useTx();
  const navigate = useNavigate();
  const { show } = useLightbox();

  const hh = settings.happyHour || {};
  if (hh.enabled === false) return null;

  const specials = hh.specials || [];
  const img = hh.image ? resolveImg(hh.image) : "";
  const isDrinks = variant === "drinks";

  return (
    <section id="happy-hour" className="relative py-20 md:py-28 overflow-hidden bg-cantina-cocoadeep text-cantina-cream" data-testid="happyhour-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 82% 18%, rgba(23,169,155,0.20), transparent 58%)" }} />
      <div className="absolute inset-0 tile-watermark opacity-[0.05]" />

      <div className="relative max-w-[1300px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {img && (
          <Reveal className={isDrinks ? "order-1" : "order-2 lg:order-1"}>
            <button
              onClick={() => show([{ img: hh.image, name: "Happy Hour Specials" }], 0)}
              className="frame-img block w-full max-w-md mx-auto rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_70px_rgba(0,0,0,0.45)] cursor-zoom-in"
              data-testid="happyhour-image"
              aria-label="View Happy Hour poster"
            >
              <img src={img} alt="Happy Hour Specials" className="w-full h-auto object-cover" loading="lazy" />
            </button>
          </Reveal>
        )}

        <div className={isDrinks ? "order-2" : "order-1 lg:order-2"}>
          <Reveal>
            <p className="font-script text-cantina-marigold mb-1" style={txtStyle(tx("happyhour.eyebrow"))}>{tx("happyhour.eyebrow").text}</p>
            <h2 className="font-display tracking-tight leading-[1.02]" style={txtStyle(tx("happyhour.heading"))}>{tx("happyhour.heading").text}</h2>
            <div className="flex items-start gap-2.5 mt-4 text-cantina-cream/75">
              <Clock size={16} className="mt-1 shrink-0 text-cantina-marigold" />
              <p className="font-sans leading-relaxed" style={txtStyle(tx("happyhour.schedule"))}>{multiline(tx("happyhour.schedule").text)}</p>
            </div>
          </Reveal>

          {specials.length > 0 && (
            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-0.5">
              {specials.map((sp, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="flex items-baseline justify-between gap-3 border-b border-white/10 py-2.5" data-testid={`happyhour-special-${i}`}>
                    <span className="font-display" style={txtStyle(tx("happyhour.specialName"))}>{sp.name}</span>
                    <span className="font-sans text-cantina-marigold shrink-0" style={txtStyle(tx("happyhour.specialPrice"))}>{sp.price}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          {!isDrinks && (
            <Reveal>
              <button
                onClick={() => navigate("/drinks")}
                className="btn-primary mt-9 inline-flex items-center gap-2 bg-cantina-marigold text-cantina-cocoa font-sans tracking-wide px-8 py-4 rounded-full"
                style={{ boxShadow: "0 12px 30px rgba(242,182,50,0.35)", ...txtStyle(tx("happyhour.ctaLabel")) }}
                data-testid="happyhour-cta"
              >
                {tx("happyhour.ctaLabel").text} <ArrowRight size={16} />
              </button>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
